import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Swal from "sweetalert2";
import {
  availabilityRulesApi,
  AvailabilityRule,
} from "../../api/availabilityRules";
import { nowMealsApi } from "../../api/nowMeals";
import { nowCombosApi } from "../../api/nowCombos";
import { AvailabilityRuleDrawer } from "../../components/kinnow/AvailabilityRuleDrawer";

const DAYS_MAP = [
  { day: 1, label: "Mon" },
  { day: 2, label: "Tue" },
  { day: 3, label: "Wed" },
  { day: 4, label: "Thu" },
  { day: 5, label: "Fri" },
  { day: 6, label: "Sat" },
  { day: 7, label: "Sun" },
];

const AvailabilityRulesTab: React.FC = () => {
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [allMeals, setAllMeals] = useState<any[]>([]);
  const [allCombos, setAllCombos] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterMealId, setFilterMealId] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [filterItemType, setFilterItemType] = useState<string>("");

  // Drawer State
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<AvailabilityRule | null>(null);

  const fetchMealsAndCombos = async () => {
    try {
      const [mealsRes, combosRes] = await Promise.all([
        nowMealsApi.getNowMeals({ limit: 1000, isActive: true }),
        nowCombosApi.getAllCombosAdmin(),
      ]);
      setAllMeals(mealsRes.nowMeals || []);
      setAllCombos(combosRes.combos || []);
    } catch (err) {
      console.error("Failed to load catalog meals or combos", err);
    }
  };

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await availabilityRulesApi.getRules({
        mealId: filterMealId || undefined,
        scheduleType: filterType || undefined,
        itemType: filterItemType || undefined,
      });
      setRules(res.rules || []);
    } catch (err) {
      console.error("Failed to load availability rules", err);
      Swal.fire("Error", "Could not load availability rules", "error");
    } finally {
      setLoading(false);
    }
  }, [filterMealId, filterType, filterItemType]);

  useEffect(() => {
    void fetchMealsAndCombos();
  }, []);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const handleOpenCreateDrawer = () => {
    setEditingRule(null);
    setDrawerOpen(true);
  };

  const handleOpenEditDrawer = (rule: AvailabilityRule) => {
    setEditingRule(rule);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingRule(null);
  };

  const handleToggleActive = async (rule: AvailabilityRule) => {
    try {
      await availabilityRulesApi.toggleRule(rule._id);
      await loadRules();
    } catch (err) {
      console.error("Toggle rule error", err);
      Swal.fire("Error", "Failed to toggle rule", "error");
    }
  };

  const handleDeleteRule = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Availability Rule?",
      text: "Are you sure you want to remove this recurring rule?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        await availabilityRulesApi.deleteRule(id);
        await loadRules();
      } catch (err) {
        console.error("Delete rule error", err);
        Swal.fire("Error", "Failed to delete rule", "error");
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      {/* Header & Filter Controls */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", lg: "center" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: { xs: "100%", lg: "auto" }, minWidth: 0, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 160 } }}>
              <InputLabel>Classification</InputLabel>
              <Select
                label="Classification"
                value={filterItemType}
                onChange={(e) => setFilterItemType(e.target.value)}
              >
                <MenuItem value="">All Items</MenuItem>
                <MenuItem value="NowMeal">Dishes & Add-ons</MenuItem>
                <MenuItem value="NowCombo">Meal Combos</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 200 } }}>
              <InputLabel>Filter by Dish</InputLabel>
              <Select
                label="Filter by Dish"
                value={filterMealId}
                onChange={(e) => setFilterMealId(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                <MenuItem value="">All Dishes</MenuItem>
                {allMeals.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 160 } }}>
              <InputLabel>Schedule Type</InputLabel>
              <Select
                label="Schedule Type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="WEEKLY">Weekly Days</MenuItem>
                <MenuItem value="DATE_RANGE">Date Range</MenuItem>
                <MenuItem value="SPECIFIC_DATES">Specific Dates</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadRules}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
          </Stack>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDrawer}
            sx={{ textTransform: "none", fontWeight: 600, width: { xs: "100%", md: "auto" } }}
          >
            Create Availability Rule
          </Button>
        </Stack>
      </Paper>

      {/* Rules Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", maxWidth: "100%", overflowX: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <EventRepeatIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No availability rules configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create recurring rules to automate daily availability and inventory.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDrawer}
              sx={{ mt: 2, textTransform: "none" }}
            >
              Add First Rule
            </Button>
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Target Item</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rule Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Schedule</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Capacity</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Priority
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule._id} hover sx={{ opacity: rule.isActive ? 1 : 0.6 }}>
                  {/* Target Item */}
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={rule.mealId?.image}
                        variant="rounded"
                        sx={{ width: 40, height: 40 }}
                      >
                        {rule.itemType === "NowCombo" ? <FastfoodIcon /> : <RestaurantMenuIcon />}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight={600}>
                            {rule.mealId?.title || rule.mealId?.name || "Item"}
                          </Typography>
                          <Chip
                            label={rule.itemType === "NowCombo" ? "Combo" : "Dish"}
                            size="small"
                            color={rule.itemType === "NowCombo" ? "secondary" : "default"}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          ₹{rule.mealId?.comboPrice || rule.mealId?.currentPrice}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Rule Name */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.ruleName || "Unnamed Rule"}
                    </Typography>
                  </TableCell>

                  {/* Schedule */}
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Chip
                        label={rule.scheduleType}
                        size="small"
                        color={
                          rule.scheduleType === "SPECIFIC_DATES"
                            ? "secondary"
                            : rule.scheduleType === "DATE_RANGE"
                            ? "primary"
                            : "default"
                        }
                        sx={{ width: "fit-content", fontWeight: 600, fontSize: 11 }}
                      />

                      {rule.scheduleType === "WEEKLY" && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          {DAYS_MAP.map(({ day, label }) => {
                            const isSelected = rule.daysOfWeek?.includes(day);
                            return (
                              <Chip
                                key={day}
                                label={label}
                                size="small"
                                color={isSelected ? "primary" : "default"}
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: isSelected ? 700 : 400,
                                  opacity: isSelected ? 1 : 0.4,
                                }}
                              />
                            );
                          })}
                        </Stack>
                      )}

                      {rule.scheduleType === "DATE_RANGE" && (
                        <Typography variant="caption" color="text.secondary">
                          {rule.startDate} to {rule.endDate}
                        </Typography>
                      )}

                      {rule.scheduleType === "SPECIFIC_DATES" && (
                        <Typography variant="caption" color="text.secondary">
                          {(rule.specificDates || []).join(", ")}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Capacity */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {rule.stockMode === "UNLIMITED" ? "Unlimited" : `${rule.defaultQuantity} units/day`}
                    </Typography>
                    {rule.stockMode === "LIMITED" &&
                      rule.dayQuantities &&
                      Object.keys(rule.dayQuantities).length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Custom per-day quantities enabled
                        </Typography>
                      )}
                  </TableCell>

                  {/* Priority */}
                  <TableCell align="center">
                    <Chip label={rule.priority || 10} size="small" variant="outlined" />
                  </TableCell>

                  {/* Status Toggle */}
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.isActive}
                      onChange={() => handleToggleActive(rule)}
                      color="success"
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit Rule">
                        <IconButton size="small" onClick={() => handleOpenEditDrawer(rule)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Rule">
                        <IconButton size="small" onClick={() => handleDeleteRule(rule._id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Availability Rule Slide-over Drawer */}
      <AvailabilityRuleDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        editingRule={editingRule}
        allMeals={allMeals}
        allCombos={allCombos}
        existingRules={rules}
        onSaved={loadRules}
      />
    </Box>
  );
};

export default AvailabilityRulesTab;
